'use client';

import Link from 'next/link';

interface EntityTypeCount {
  entity_type: string;
  count: number;
}

interface EntityCategory {
  category: string;
  count: number;
}

interface EntityCoverageWidgetProps {
  entitiesByType: EntityTypeCount[];
  entitiesByCategory: EntityCategory[];
  totalEntities: number;
  totalMappings: number;
  loading?: boolean;
}

const ENTITY_TYPE_ICONS: Record<string, string> = {
  technology: '💻',
  infrastructure: '🏗️',
  application: '📱',
  data_store: '🗄️',
  api: '🔌',
  team: '👥',
  vendor: '🏢',
  process: '⚙️',
  capability: '🎯',
  product: '📦',
  stakeholder: '👤',
  compliance_framework: '📋',
  control: '🔒',
  risk: '⚠️',
  policy: '📜',
  architecture_decision: '🏛️',
  tech_debt: '💳',
  migration_plan: '🚀',
  incident: '🚨',
  sla: '📊',
  metric: '📈',
};

const CATEGORY_COLORS: Record<string, string> = {
  Technology: 'bg-blue-500',
  Business: 'bg-purple-500',
  Governance: 'bg-green-500',
  Architecture: 'bg-accent',
  Operations: 'bg-red-500',
};

export function EntityCoverageWidget({
  entitiesByType,
  entitiesByCategory,
  totalEntities,
  totalMappings,
  loading,
}: EntityCoverageWidgetProps) {
  if (loading) {
    return (
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <h3 className="text-lg font-semibold text-light-text dark:text-foreground mb-4">Entity Coverage</h3>
        <div className="animate-pulse">
          <div className="h-32 bg-light-border dark:bg-background rounded mb-4" />
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-6 bg-light-border dark:bg-background rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const mappingPercentage = totalEntities > 0
    ? Math.round((totalMappings / totalEntities) * 100)
    : 0;

  return (
    <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-light-text dark:text-foreground">Entity Coverage</h3>
        <Link href="/command-center/entities3" className="text-sm text-accent hover:underline">
          View All
        </Link>
      </div>

      {totalEntities === 0 ? (
        <div className="text-center py-8 text-light-text-secondary dark:text-foreground-tertiary">
          <div className="text-4xl mb-2">📊</div>
          <p className="text-sm">No entities created yet</p>
          <Link href="/command-center/entities3" className="text-accent text-sm hover:underline mt-2 inline-block">
            Create your first entity
          </Link>
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center p-3 bg-light-border/50 dark:bg-background rounded-lg">
              <div className="text-2xl font-bold text-light-text dark:text-foreground">{totalEntities}</div>
              <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">Total Entities</div>
            </div>
            <div className="text-center p-3 bg-light-border/50 dark:bg-background rounded-lg">
              <div className="text-2xl font-bold text-accent">{mappingPercentage}%</div>
              <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">Mapped to ArchiMate</div>
            </div>
          </div>

          {/* Category breakdown */}
          <div className="mb-4">
            <div className="text-sm font-medium text-light-text-secondary dark:text-foreground-secondary mb-2">
              By Category
            </div>
            <div className="flex h-4 rounded-full overflow-hidden">
              {entitiesByCategory.map((cat) => {
                const percentage = (cat.count / totalEntities) * 100;
                return (
                  <div
                    key={cat.category}
                    className={`${CATEGORY_COLORS[cat.category] || 'bg-gray-400'} transition-all`}
                    style={{ width: `${percentage}%` }}
                    title={`${cat.category}: ${cat.count}`}
                  />
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {entitiesByCategory.map((cat) => (
                <div key={cat.category} className="flex items-center gap-1 text-xs">
                  <div className={`w-2 h-2 rounded-full ${CATEGORY_COLORS[cat.category] || 'bg-gray-400'}`} />
                  <span className="text-light-text-secondary dark:text-foreground-secondary">
                    {cat.category} ({cat.count})
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top entity types */}
          <div>
            <div className="text-sm font-medium text-light-text-secondary dark:text-foreground-secondary mb-2">
              Top Entity Types
            </div>
            <div className="space-y-2">
              {entitiesByType.slice(0, 5).map((type) => (
                <div key={type.entity_type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{ENTITY_TYPE_ICONS[type.entity_type] || '📁'}</span>
                    <span className="text-sm text-light-text dark:text-foreground capitalize">
                      {type.entity_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-light-text-secondary dark:text-foreground-secondary">
                    {type.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
